import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MathRenderer from '../MathRenderer';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 1rem;
  font-family: 'Cairo', sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.3s ease;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  margin: 0 2rem;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #e74c3c, #3498db);
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const TestContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const QuestionNumber = styled.div`
  text-align: center;
  font-size: 1.2rem;
  color: #7f8c8d;
  margin-bottom: 1rem;
`;

const QuestionText = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.4;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const OptionButton = styled.button<{ selected: boolean }>`
  padding: 1rem 1.5rem;
  border: 2px solid ${props => props.selected ? '#3498db' : '#e0e0e0'};
  background: ${props => props.selected ? '#e3f2fd' : 'white'};
  border-radius: 12px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  
  &:hover {
    border-color: #3498db;
    background: #f8f9fa;
  }
`;

const NextButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(52, 152, 219, 0.3);
  }
  
  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Timer = styled.div`
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #e74c3c;
  margin-bottom: 1rem;
`;

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  isMath?: boolean;
}

interface Props {
  testType: 'english' | 'math';
  testLanguage: 'english' | 'arabic';
  onTestComplete: (score: number, totalQuestions: number) => void;
  onBack: () => void;
  language: 'ar' | 'en';
}

const TestComponent: React.FC<Props> = ({ testType, testLanguage, onTestComplete, onBack, language }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<number[]>([]);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes

  // Sample questions - in a real app, these would come from an API
  const questions: Question[] = testType === 'english' ? [
    {
      id: 1,
      question: "What is the synonym of 'abundant'?",
      options: ["Scarce", "Plentiful", "Limited", "Rare"],
      correctAnswer: 1
    },
    {
      id: 2,
      question: "Choose the correct sentence:",
      options: [
        "She don't like coffee",
        "She doesn't likes coffee", 
        "She doesn't like coffee",
        "She not like coffee"
      ],
      correctAnswer: 2
    },
    // Add more English questions...
  ] : [
    {
      id: 1,
      question: testLanguage === 'arabic' ? "احسب: \\frac{5}{3} - \\left( \\frac{7}{2} - \\frac{7}{5}\\right) =" : "Calculate: \\frac{5}{3} - \\left( \\frac{7}{2} - \\frac{7}{5}\\right) =",
      options: ["-\\frac{2}{3}", "-\\frac{1}{30}", "-\\frac{13}{30}", "-\\frac{97}{30}"],
      correctAnswer: 2,
      isMath: true
    },
    {
      id: 2,
      question: testLanguage === 'arabic' ? "إذا كان x = 5، فما قيمة \\frac{3x + 2}{4}؟" : "If x = 5, what is \\frac{3x + 2}{4}?",
      options: ["\\frac{15}{4}", "\\frac{17}{4}", "\\frac{19}{4}", "\\frac{21}{4}"],
      correctAnswer: 1,
      isMath: true
    },
    {
      id: 3,
      question: testLanguage === 'arabic' ? "حل المعادلة: 2x + 7 = 15" : "Solve: 2x + 7 = 15",
      options: ["x = 3", "x = 4", "x = 5", "x = 6"],
      correctAnswer: 1
    },
    {
      id: 4,
      question: testLanguage === 'arabic' ? "ما هو ناتج \\sqrt{64} + \\sqrt{36}؟" : "What is \\sqrt{64} + \\sqrt{36}?",
      options: ["12", "14", "16", "18"],
      correctAnswer: 1,
      isMath: true
    },
    // Add more Math questions...
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleTestComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNext = () => {
    if (selectedAnswer !== null) {
      const newAnswers = [...answers];
      newAnswers[currentQuestion] = selectedAnswer;
      setAnswers(newAnswers);

      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
      } else {
        handleTestComplete();
      }
    }
  };

  const handleTestComplete = () => {
    const finalAnswers = [...answers];
    if (selectedAnswer !== null) {
      finalAnswers[currentQuestion] = selectedAnswer;
    }
    
    const score = finalAnswers.reduce((acc, answer, index) => {
      return acc + (answer === questions[index]?.correctAnswer ? 1 : 0);
    }, 0);
    
    onTestComplete(score, questions.length);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const content = {
    ar: {
      question: 'سؤال',
      of: 'من',
      next: 'التالي',
      finish: 'إنهاء'
    },
    en: {
      question: 'Question',
      of: 'of',
      next: 'Next',
      finish: 'Finish'
    }
  };

  const currentContent = content[language];

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>←</BackButton>
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
      </Header>

      <Timer>{formatTime(timeLeft)}</Timer>

      <TestContent>
        <QuestionNumber>
          {currentContent.question} {currentQuestion + 1} {currentContent.of} {questions.length}
        </QuestionNumber>

        <QuestionText>
          {questions[currentQuestion]?.isMath ? (
            <MathRenderer latex={questions[currentQuestion]?.question} />
          ) : (
            questions[currentQuestion]?.question
          )}
        </QuestionText>

        <OptionsContainer>
          {questions[currentQuestion]?.options.map((option, index) => (
            <OptionButton
              key={index}
              selected={selectedAnswer === index}
              onClick={() => handleAnswerSelect(index)}
            >
              {questions[currentQuestion]?.isMath ? (
                <MathRenderer latex={option} />
              ) : (
                option
              )}
            </OptionButton>
          ))}
        </OptionsContainer>

        <NextButton 
          onClick={handleNext}
          disabled={selectedAnswer === null}
        >
          {currentQuestion === questions.length - 1 ? currentContent.finish : currentContent.next}
        </NextButton>
      </TestContent>
    </Container>
  );
};

export default TestComponent;
